import { doc, deleteDoc, deleteField, updateDoc, setDoc, getDoc, collection, getDocs, arrayRemove, writeBatch } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../firebase";
import { FIREBASE_COLLECTIONS } from "../config/firebaseCollections";
import { buildStyleOverridesWithSection } from "utils/classStyleDefaults";
import {
  fetchClassContentBoxesOrder,
  getClassRef,
  getContentBoxRef,
  replaceContentBoxIdInOrder,
  resolveLinkedClassIds,
  syncLinkedClassIdsIndex,
  updateLinkedContentBoxes,
  uniqueClassIds,
} from "utils/linkedContentBoxes";
import { removeContentBoxIdsFromOrderBatch } from "utils/contentBoxOrderAppend";
import {
  isMissingContentBoxUpdateError,
  updateExistingContentBoxDoc,
} from "./contentBoxDocUpdate";
import { mapSnapshotToContentBoxes } from "./contentBoxPresentQuery";
import { reportAppError } from "./reportAppError";
import { trackAppMetric } from "./appMetrics";
import { saveContentBoxActiveDatesWithLinkedScope } from "./linkedActiveDatesSave";

export { updateLinkedContentBoxes } from "utils/linkedContentBoxes";

/**
 * Deletes a content box from Firestore and removes it from the contentBoxesOrder list.
 * @param {string} userId - The user's UID
 * @param {string} classId - The class ID
 * @param {string} contentBoxId - The ID of the content box to delete
 * @returns {Promise<boolean>} - Returns true if successful, false if failed
 */
 // Track ongoing delete operations to prevent race conditions
const ongoingDeletes = new Set();
const CONTENT_BOX_ZOOM_MIN = 0.5;
const CONTENT_BOX_ZOOM_MAX = 2;

const clampContentBoxZoom = (value) => (
  Math.min(CONTENT_BOX_ZOOM_MAX, Math.max(CONTENT_BOX_ZOOM_MIN, value))
);

async function deleteContentBoxDocViaRest(userId, classId, contentBoxId) {
  const auth = getAuth();
  const projectId = db?.app?.options?.projectId || "lesson-launchpad";
  const useFirebaseEmulators = process.env.REACT_APP_USE_FIREBASE_EMULATORS === "true";
  const firestoreEmulatorPort = process.env.REACT_APP_FIRESTORE_EMULATOR_PORT || "8085";
  if (useFirebaseEmulators && projectId === "lesson-launchpad") {
    throw new Error("Refusing REST delete against production Firebase while emulator mode is enabled.");
  }
  const docPath = `${FIREBASE_COLLECTIONS.USERS}/${userId}/${FIREBASE_COLLECTIONS.CLASSES}/${classId}/${FIREBASE_COLLECTIONS.CONTENT_BOXES}/${contentBoxId}`;
  const endpoint = useFirebaseEmulators
    ? `http://127.0.0.1:${firestoreEmulatorPort}/v1/projects/${projectId}/databases/(default)/documents/${docPath}`
    : `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents/${docPath}`;
  const token = await auth?.currentUser?.getIdToken?.();
  if (!useFirebaseEmulators && !token) {
    throw new Error("REST delete unavailable: missing auth token");
  }
  const response = await fetch(endpoint, {
    method: "DELETE",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!response.ok && response.status !== 404) {
    const body = await response.text();
    throw new Error(`REST delete failed (${response.status}): ${body.slice(0, 300)}`);
  }
  return response.status;
}

export const deleteContentBox = async (userId, classId, contentBoxId) => {
  console.log("🔧 deleteContentBox args", { userId, classId, contentBoxId });
  
  if (!userId || !classId || !contentBoxId) {
    console.error("❌ Missing required parameters for deleteContentBox");
    return false;
  }
  
  // Prevent multiple simultaneous deletes of the same content box
  const deleteKey = `${userId}-${classId}-${contentBoxId}`;
  if (ongoingDeletes.has(deleteKey)) {
    console.log("⚠️ Delete operation already in progress for this content box, skipping...");
    return false;
  }
  
  ongoingDeletes.add(deleteKey);
  console.log("🔧 Starting delete operation for:", deleteKey);
  
  try {
    console.log("🔧 Trying to deleteContentBox");
    console.log("🔧 FIREBASE_COLLECTIONS:", FIREBASE_COLLECTIONS);
    console.log("🔧 db instance:", db);
    console.log("🔧 userId:", userId);
    console.log("🔧 classId:", classId);
    console.log("🔧 contentBoxId:", contentBoxId);
    
    const classRef = doc(db, FIREBASE_COLLECTIONS.USERS, userId, FIREBASE_COLLECTIONS.CLASSES, classId);
    const contentBoxRef = doc(classRef, FIREBASE_COLLECTIONS.CONTENT_BOXES, contentBoxId);
    console.log("🔧 classRef => ", classRef);
    console.log("🔧 contentBoxRef => ", contentBoxRef);
    
    // Test if we can access the database at all
    console.log("🔧 Testing database connection...");
    try {
      const testRef = doc(db, FIREBASE_COLLECTIONS.USERS, userId);
      console.log("🔧 Test reference created successfully");
    } catch (testError) {
      console.error("❌ Failed to create test reference:", testError);
    }

    // 🔹 Fetch the class document
    console.log("🔧 About to fetch class document...");
    
    // Add timeout to prevent hanging
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Database operation timed out after 10 seconds')), 10000)
    );
    
    const classSnap = await Promise.race([
      getDoc(classRef),
      timeoutPromise
    ]);
    
    console.log("🔧 Class document fetched successfully");
    console.log("🔧 Class document exists:", classSnap.exists());
    
    if (!classSnap.exists()) {
      console.error("❌ Class document not found.");
      return false;
    }

    // 🔹 Check if this is the last content box and handle special case
    const currentClassData = classSnap.data();
    const currentOrder = currentClassData.contentBoxesOrder || [];
    const isLastContentBox = currentOrder.length === 1 && currentOrder.includes(contentBoxId);
    
    console.log("🔧 Current contentBoxesOrder:", currentOrder);
    console.log("🔧 Is this the last content box in the class?", isLastContentBox);
    
    if (isLastContentBox) {
      console.log("🔧 Last content box detected - implementing workaround...");
      
      // First, let's check if the content box exists and get its data
      const contentBoxSnap = await getDoc(contentBoxRef);
      if (!contentBoxSnap.exists()) {
        console.log("⚠️ Content box doesn't exist, skipping deletion");
      } else {
        const contentBoxData = contentBoxSnap.data();
        console.log("🔧 Content box data keys:", Object.keys(contentBoxData));
        
        // Check if the content box has active dates that might match current date
        const hasActiveDates = contentBoxData.startDate || contentBoxData.endDate;
        console.log("🔧 Content box has active dates:", hasActiveDates);
        
        if (hasActiveDates) {
          console.log("🔧 Setting active dates to past date to avoid race condition...");
          
          // Set the start date to a date in the past (e.g., January 1, 2020)
          const pastDate = new Date('2020-01-01');
          const pastDateString = pastDate.toISOString().split('T')[0]; // YYYY-MM-DD format
          
          try {
            await updateDoc(contentBoxRef, {
              startDate: pastDateString,
              endDate: pastDateString
            });
            console.log("✅ Updated content box active dates to past date:", pastDateString);
            
            // Add a small delay to ensure the update propagates
            await new Promise(resolve => setTimeout(resolve, 100));
            console.log("✅ Waited for update to propagate");
          } catch (updateError) {
            console.log("⚠️ Failed to update active dates, proceeding with deletion anyway:", updateError.message);
          }
        }
      }
    }

    // 🔹 Delete the content box document
    console.log("🔧 About to delete content box document...");
    
    // Check if the content box exists before trying to delete it
    console.log("🔧 Checking if content box exists before deletion...");
    const contentBoxSnap = await getDoc(contentBoxRef);
    console.log("🔧 Content box exists:", contentBoxSnap.exists());
    
    if (!contentBoxSnap.exists()) {
      console.log("⚠️ Content box doesn't exist, skipping deletion");
    } else {
      console.log("🔧 Content box exists, proceeding with deletion...");
      
      console.log("🔧 Starting deleteDoc operation...");
      try {
        // Let's also try creating a fresh reference to see if that helps
        const freshContentBoxRef = doc(db, FIREBASE_COLLECTIONS.USERS, userId, FIREBASE_COLLECTIONS.CLASSES, classId, FIREBASE_COLLECTIONS.CONTENT_BOXES, contentBoxId);
        console.log("🔧 Fresh content box reference created");
        
        console.log("🔧 About to execute deleteDoc...");
        
        // Try the regular delete first
        try {
          console.log("🔧 Attempting regular deleteDoc...");
          
          const deleteTimeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Delete operation timed out after 10 seconds')), 10000)
          );
          
          console.log("🔧 About to call deleteDoc with timeout...");
          await Promise.race([
            deleteDoc(freshContentBoxRef),
            deleteTimeoutPromise
          ]);
          console.log(`✅ Content box ${contentBoxId} deleted successfully!`);
        } catch (firstDeleteError) {
          console.log("⚠️ Regular delete failed, trying batch operation...");
          console.log("⚠️ First delete error:", firstDeleteError.message);
          console.log("⚠️ First delete error code:", firstDeleteError.code);
          console.log("⚠️ First delete error stack:", firstDeleteError.stack);
          
          // Fallback: Try using a batch operation
          const batch = writeBatch(db);
          batch.delete(freshContentBoxRef);
          
          const batchTimeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Batch operation timed out after 10 seconds')), 10000)
          );
          
          console.log("🔧 Attempting batch delete...");
          await Promise.race([
            batch.commit(),
            batchTimeoutPromise
          ]);
          
          console.log(`✅ Content box ${contentBoxId} deleted successfully via batch!`);
        }
      } catch (deleteError) {
        console.error("❌ All delete operations failed:", deleteError);
        console.error("❌ Delete error code:", deleteError.code);
        console.error("❌ Delete error message:", deleteError.message);
        
        // If it's a permission error, let's try to provide more specific info
        if (deleteError.code === 'permission-denied') {
          console.error("❌ This appears to be a permission issue. Check Firebase security rules.");
        } else if (deleteError.code === 'not-found') {
          console.error("❌ Document not found - it may have been deleted already.");
        } else if (deleteError.code === 'unavailable') {
          console.error("❌ Service unavailable - check network connection.");
        }
        
        throw deleteError;
      }
    }

    // 🔹 Remove the contentBoxId from the contentBoxesOrder array
    console.log("🔧 About to update contentBoxesOrder...");
    
    // We already have the current order from earlier, but let's get a fresh snapshot
    const freshClassSnap = await getDoc(classRef);
    if (!freshClassSnap.exists()) {
      console.log("⚠️ Class document no longer exists, skipping order update");
      return true;
    }
    
    const freshClassData = freshClassSnap.data();
    const freshOrder = freshClassData.contentBoxesOrder || [];
    console.log("🔧 Fresh contentBoxesOrder:", freshOrder);
    
    // Only update if the contentBoxId is actually in the array
    if (freshOrder.includes(contentBoxId)) {
      console.log("🔧 Content box ID found in order array, removing...");
      
      // Check if this is the last content box in the class
      const isLastContentBox = freshOrder.length === 1;
      console.log("🔧 Is this the last content box in the class?", isLastContentBox);
      
      if (isLastContentBox) {
        console.log("🔧 Handling deletion of last content box in class...");
        
        // For the last content box, we might need to handle this differently
        // Let's try setting the array to an empty array instead of using arrayRemove
        const updateTimeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Update operation timed out after 10 seconds')), 10000)
        );
        
        await Promise.race([
          updateDoc(classRef, {
            contentBoxesOrder: []
          }),
          updateTimeoutPromise
        ]);
        
        console.log(`✅ Set contentBoxesOrder to empty array (last content box deleted).`);
      } else {
        console.log("🔧 Using arrayRemove for non-last content box...");
        
        const updateTimeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Update operation timed out after 10 seconds')), 10000)
        );
        
        await Promise.race([
          updateDoc(classRef, {
            contentBoxesOrder: arrayRemove(contentBoxId)
          }),
          updateTimeoutPromise
        ]);

        console.log(`✅ Removed ${contentBoxId} from contentBoxesOrder.`);
      }
    } else {
      console.log("⚠️ Content box ID not found in order array, skipping update.");
    }
    
    console.log("✅ deleteContentBox function completed successfully!");
    return true;
  } catch (error) {
    console.error("❌ Error deleting content box:", error);
    reportAppError(error, {
      feature: "content_box",
      action: "delete",
      severity: "error",
      classId,
      contentBoxId,
      uid: userId,
    });
    // Re-throw the error so callers can handle it appropriately
    throw error;
  } finally {
    // Always clean up the ongoing delete tracking
    ongoingDeletes.delete(deleteKey);
    console.log("🔧 Cleaned up delete operation tracking for:", deleteKey);
  }
};

// const withTimeout = (p, ms, label) =>
//   Promise.race([
//     p,
//     new Promise((_, rej) => setTimeout(
//       () => rej(new Error(`⏱ Timeout after ${ms}ms @ ${label}`)), ms
//     ))
//   ]);

// export const deleteContentBox = async (userId, classId, contentBoxId) => {
//   console.log('🔧 deleteContentBox args', { userId, classId, contentBoxId });

//   try {
//     // 🔴 Double-check you’re on the right root collection (usersNew vs users)
//     const classRef = doc(db, 'usersNew', userId, 'classes', classId);
//     const contentBoxRef = doc(classRef, 'contentBoxes', contentBoxId);

//     // ✅ Prove the class exists on the server (not cache)
//     const classSnap = await withTimeout(
//       getDocFromServer(classRef),
//       6000,
//       'getDocFromServer(classRef)'
//     );
//     if (!classSnap.exists()) {
//       console.error('❌ Class doc not found on server.');
//       return;
//     }

//     // 🗑 Delete the content box
//     await withTimeout(
//       deleteDoc(contentBoxRef),
//       8000,
//       'deleteDoc(contentBoxRef)'
//     );
//     console.log(`✅ Content box ${contentBoxId} deleted on server`);

//     // 🧹 Remove id from order array
//     await withTimeout(
//       updateDoc(classRef, { contentBoxesOrder: arrayRemove(contentBoxId) }),
//       8000,
//       'updateDoc(contentBoxesOrder)'
//     );
//     console.log(`✅ Removed ${contentBoxId} from contentBoxesOrder`);

//     // 🔍 Verify deletion from the server
//     const verify = await withTimeout(
//       getDocFromServer(contentBoxRef),
//       6000,
//       'verify getDocFromServer(contentBoxRef)'
//     );
//     console.log('🔎 Exists after delete?', verify.exists());
//   } catch (err) {
//     console.error('❌ Delete failed', {
//       code: err.code,
//       message: err.message,
//       stack: err.stack
//     });
//   }
// };


/**
 * Deletes multiple content boxes with structured per-box results.
 * Uses REST DELETE per document (calendar-safe) and updates contentBoxesOrder
 * for successfully deleted ids only.
 */
export async function executeMultipleContentBoxDelete(userId, classId, contentBoxIds) {
  const normalizedIds = [...new Set((contentBoxIds || []).map((id) => String(id || "")).filter(Boolean))];

  if (!userId || !classId || !normalizedIds.length) {
    return {
      ok: false,
      deletedIds: [],
      failedIds: [],
      partialWrite: false,
      nextContentBoxesOrder: null,
      error: "Missing required parameters: User ID, Class ID, or Content Box IDs.",
    };
  }

  const classRef = doc(db, FIREBASE_COLLECTIONS.USERS, userId, FIREBASE_COLLECTIONS.CLASSES, classId);

  let classSnap;
  try {
    classSnap = await getDoc(classRef);
  } catch (error) {
    console.error("Failed to load class for multi-delete:", error);
    reportAppError(error, {
      feature: "content_box",
      action: "delete_load_class",
      severity: "error",
      classId,
      uid: userId,
      extra: {
        contentBoxCount: normalizedIds.length,
      },
    });
    return {
      ok: false,
      deletedIds: [],
      failedIds: normalizedIds.map((contentBoxId) => ({
        contentBoxId,
        reason: "delete_failed",
      })),
      partialWrite: false,
      nextContentBoxesOrder: null,
      error: "Class document could not be loaded.",
    };
  }

  if (!classSnap.exists()) {
    console.error("Class document not found.");
    return {
      ok: false,
      deletedIds: [],
      failedIds: normalizedIds.map((contentBoxId) => ({
        contentBoxId,
        reason: "delete_failed",
      })),
      partialWrite: false,
      nextContentBoxesOrder: null,
      error: "Class document not found.",
    };
  }

  const currentOrder = Array.isArray(classSnap.data()?.contentBoxesOrder)
    ? classSnap.data().contentBoxesOrder
    : [];

  const deletedIds = [];
  const failedIds = [];

  for (const targetId of normalizedIds) {
    try {
      await deleteContentBoxDocViaRest(userId, classId, targetId);
      deletedIds.push(targetId);
    } catch (error) {
      console.error("Failed to delete content box:", targetId, error);
      reportAppError(error, {
        feature: "content_box",
        action: "delete",
        severity: "error",
        classId,
        contentBoxId: targetId,
        uid: userId,
      });
      failedIds.push({
        contentBoxId: targetId,
        reason: "delete_failed",
      });
    }
  }

  if (deletedIds.length === 0) {
    return {
      ok: false,
      deletedIds,
      failedIds,
      partialWrite: false,
      nextContentBoxesOrder: null,
      error: "Unable to delete selected boxes.",
    };
  }

  const deletedSet = new Set(deletedIds.map((id) => String(id)));
  const nextContentBoxesOrder = currentOrder.filter((id) => !deletedSet.has(String(id)));

  try {
    await updateDoc(classRef, {
      contentBoxesOrder: arrayRemove(...deletedIds),
    });
  } catch (error) {
    console.error("Failed to update contentBoxesOrder after delete:", error);
    reportAppError(error, {
      feature: "content_box",
      action: "delete_order_update",
      severity: "error",
      classId,
      uid: userId,
      extra: {
        deletedCount: deletedIds.length,
      },
    });
    return {
      ok: false,
      deletedIds,
      failedIds,
      partialWrite: true,
      orderUpdateFailed: true,
      nextContentBoxesOrder: null,
      error: "Some boxes may have been deleted but could not be fully removed. Please refresh before retrying.",
    };
  }

  const partialWrite = failedIds.length > 0;
  const ok = failedIds.length === 0;

  return {
    ok,
    deletedIds,
    failedIds,
    partialWrite,
    nextContentBoxesOrder,
    error: ok ? undefined : "Some boxes could not be deleted.",
  };
}

export async function executeLinkedAwareContentBoxDelete({
  userId,
  sourceClassId,
  contentBoxId,
  classIdsToDelete,
  classes = [],
  linkedClassIdsHint,
}) {
  const normalizedDeleteClassIds = uniqueClassIds(classIdsToDelete);
  const allClassIds = uniqueClassIds((classes || []).map((classItem) => classItem?.classId));

  if (!userId || !sourceClassId || !contentBoxId || !normalizedDeleteClassIds.length) {
    return {
      ok: false,
      contentBoxId,
      deletedIds: [],
      deletedClassIds: [],
      deletedByClassId: {},
      failedIds: [],
      failedClassIds: [],
      partialWrite: false,
      error: "Missing required parameters for linked content box delete.",
    };
  }

  let linkedClassIds = await resolveLinkedClassIds(userId, contentBoxId, {
    sourceClassId,
    classIds: allClassIds,
    hintLinkedClassIds: linkedClassIdsHint,
  });
  linkedClassIds = uniqueClassIds([sourceClassId, ...linkedClassIds]);

  const linkedClassSet = new Set(linkedClassIds);
  const targetClassIds = normalizedDeleteClassIds.filter((targetClassId) => linkedClassSet.has(targetClassId));

  if (!targetClassIds.length) {
    return {
      ok: false,
      contentBoxId,
      deletedIds: [],
      deletedClassIds: [],
      deletedByClassId: {},
      failedIds: [],
      failedClassIds: [],
      partialWrite: false,
      error: "No linked classes were selected for delete.",
    };
  }

  const deletedByClassId = {};
  const failedIds = [];

  for (const targetClassId of targetClassIds) {
    const result = await executeMultipleContentBoxDelete(userId, targetClassId, [contentBoxId]);
    if (result.deletedIds?.includes(contentBoxId)) {
      deletedByClassId[targetClassId] = {
        deletedIds: result.deletedIds,
        nextContentBoxesOrder: result.nextContentBoxesOrder,
      };
    }
    if (!result.ok) {
      failedIds.push({
        contentBoxId,
        classId: targetClassId,
        reason: result.error || "delete_failed",
      });
    }
  }

  const deletedClassIds = Object.keys(deletedByClassId);
  if (!deletedClassIds.length) {
    return {
      ok: false,
      contentBoxId,
      deletedIds: [],
      deletedClassIds: [],
      deletedByClassId,
      failedIds,
      failedClassIds: failedIds.map((entry) => entry.classId),
      partialWrite: false,
      error: "Unable to delete selected linked box.",
    };
  }

  const deletedClassIdSet = new Set(deletedClassIds);
  const remainingLinkedClassIds = linkedClassIds.filter((targetClassId) => !deletedClassIdSet.has(targetClassId));

  try {
    if (remainingLinkedClassIds.length > 1) {
      await syncLinkedClassIdsIndex(userId, contentBoxId, remainingLinkedClassIds);
    } else if (remainingLinkedClassIds.length === 1) {
      const remainingBoxRef = getContentBoxRef(userId, remainingLinkedClassIds[0], contentBoxId);
      const remainingBoxSnap = await getDoc(remainingBoxRef);
      if (remainingBoxSnap.exists()) {
        await updateDoc(remainingBoxRef, {
          linkedBox: false,
          useLinkedSlidePosition: false,
          linkedClassIds: deleteField(),
          linkedInClasses: deleteField(),
          linkedSlidePosition: deleteField(),
        });
      }
    }
  } catch (error) {
    console.error("Failed to repair linked content box index after delete:", error);
    reportAppError(error, {
      feature: "content_box",
      action: "linked_delete_index_repair",
      severity: "error",
      contentBoxId,
      uid: userId,
      extra: {
        deletedClassCount: deletedClassIds.length,
        remainingLinkedClassCount: remainingLinkedClassIds.length,
      },
    });
    failedIds.push({
      contentBoxId,
      classId: remainingLinkedClassIds[0] || sourceClassId,
      reason: "index_repair_failed",
    });
  }

  const partialWrite = failedIds.length > 0;

  return {
    ok: !partialWrite,
    contentBoxId,
    deletedIds: deletedClassIds.includes(sourceClassId) ? [contentBoxId] : [],
    deletedClassIds,
    deletedByClassId,
    failedIds,
    failedClassIds: failedIds.map((entry) => entry.classId),
    partialWrite,
    remainingLinkedClassIds,
    error: partialWrite ? "Some linked box instances could not be deleted." : undefined,
  };
}

/**
 * Deletes multiple content boxes from Firestore and removes them from the contentBoxesOrder list.
 * @param {string} userId - The user's UID
 * @param {string} classId - The class ID
 * @param {string[]} contentBoxIds - An array of content box IDs to delete
 * @returns {Promise<boolean>} - Returns true if all deletes succeeded, false if none succeeded
 */
 export const deleteMultipleContentBoxes = async (userId, classId, contentBoxIds) => {
  const result = await executeMultipleContentBoxDelete(userId, classId, contentBoxIds);

  if (result.ok) {
    return true;
  }

  if (result.deletedIds.length === 0) {
    return false;
  }

  throw new Error(result.error || "Error deleting multiple content boxes");
};


/**
 * Updates a content box in Firestore
 * @param {string} userId - The user's UID
 * @param {string} classId - The class ID
 * @param {string} contentBoxId - The ID of the content box
 * @param {object} updates - The fields to update
 */
// export const updateContentBox = async (userId, classId, contentBoxId, updates) => {
//   try {
//     const contentBoxRef = doc(db, FIREBASE_COLLECTIONS.USERS, userId, "classes", classId, "contentBoxes", contentBoxId);
//     await updateDoc(contentBoxRef, updates);
//     console.log("Content box updated successfully!");
//   } catch (error) {
//     console.error("Error updating content box:", error);
//   }
// };

/**
 * @deprecated Use executeDuplicateBoxToClasses from duplicateContentBoxCopy.js instead.
 */
export const copyContentBoxToClasses = async () => {
  console.error("copyContentBoxToClasses is deprecated and no longer writes to Firestore.");
  return false;
};

/**
 * Fetches all content boxes for a specific class
 * @param {string} userId - The user's UID
 * @param {string} classId - The class ID
 * @returns {array} - Array of content box objects
 */
export const fetchContentBoxesForClass = async (userId, classId) => {
  try {
    const contentBoxesRef = collection(db, FIREBASE_COLLECTIONS.USERS, userId, "classes", classId, "contentBoxes");
    const contentBoxesSnap = await getDocs(contentBoxesRef);
    return mapSnapshotToContentBoxes(contentBoxesSnap);
  } catch (error) {
    console.error("Error fetching content boxes:", error);
    return [];
  }
};

/**
 * Sends a content box to storage by setting active to false and removing it from contentBoxesOrder
 * @param {string} userId - The user's UID
 * @param {string} classId - The class ID
 * @param {string} contentBoxId - The ID of the content box to delete
 * @returns {Promise<boolean>} - Returns true if successful, false if failed
 */
 export const sendBoxToStorage = async (userId, classId, contentBoxId) => {
  try {
    if (!userId || !classId || !contentBoxId) {
      console.error("❌ Missing required parameters: userId, classId, or contentBoxId.");
      return false;
    }

    const classRef = doc(db, FIREBASE_COLLECTIONS.USERS, userId, FIREBASE_COLLECTIONS.CLASSES, classId);
    const contentBoxRef = doc(classRef, FIREBASE_COLLECTIONS.CONTENT_BOXES, contentBoxId);

    const classSnap = await getDoc(classRef);
    if (!classSnap.exists()) {
      console.error("❌ Class not found.");
      return false;
    }

    const contentBoxSnap = await getDoc(contentBoxRef);
    if (!contentBoxSnap.exists()) {
      trackAppMetric("content_box.update", {
        outcome: "stale",
        reason: "missing_doc",
        action: "send_to_storage",
      });
      console.warn("⚠️ Content box already missing; treating send-to-storage as no-op.");
      return false;
    }

    const batch = writeBatch(db);
    batch.update(contentBoxRef, { active: false });
    removeContentBoxIdsFromOrderBatch(batch, classRef, [contentBoxId]);
    await batch.commit();

    console.log("✅ Content box moved to storage successfully.");
    return true;
  } catch (error) {
    if (isMissingContentBoxUpdateError(error)) {
      trackAppMetric("content_box.update", {
        outcome: "stale",
        reason: "not_found_update",
        action: "send_to_storage",
      });
      return false;
    }
    console.error("❌ Error moving content box to storage:", error);
    throw error;
  }
};


/**
 * Updates the background of a content box in Firestore.
 *
 * @param {string} userId - The ID of the user.
 * @param {string} classId - The ID of the class containing the content box.
 * @param {string} contentBoxId - The ID of the content box to update.
 * @param {string} imageFileName - The file name of the background image.
 * @param {boolean} isMultiBox - Whether the content box is part of a multi-box.
 * @param {Function} updateContentBoxWithinMultiBox - Function to update a multi-box content.
 * @param {Object} content - The content box object (only needed for multi-box updates).
 *
 * @returns {Promise<boolean>} A promise that resolves to true if successful, false if failed.
 */
export const saveBackgroundContentBox = async (
  userId,
  classId,
  contentBoxId,
  imageFileName,
  isMultiBox = false,
  updateContentBoxWithinMultiBox = null,
  content = null
) => {
  try {
    if (!userId || !classId || !contentBoxId || imageFileName === undefined || imageFileName === null) {
        console.error("❌ Missing required parameters: userId, classId, contentBoxId, or imageFileName.");
        return false;
    }

    // const backgroundUrl = `url(${imageFileName})`;
    
    if (isMultiBox && updateContentBoxWithinMultiBox && content) {
      updateContentBoxWithinMultiBox({
        ...content,
        background: imageFileName,
        styleOverrides: buildStyleOverridesWithSection(content.styleOverrides, "background", { value: imageFileName }),
      }, true);
    } else {
      const contentBoxRef = doc(
        db, 
        FIREBASE_COLLECTIONS.USERS, 
        userId, 
        FIREBASE_COLLECTIONS.CLASSES, 
        classId, 
        FIREBASE_COLLECTIONS.CONTENT_BOXES, 
        contentBoxId
      );

      const contentBoxSnap = await getDoc(contentBoxRef);
      if (!contentBoxSnap.exists()) {
        trackAppMetric("content_box.update", {
          outcome: "stale",
          reason: "missing_doc",
          action: "save_background",
        });
        return false;
      }

      const existingContent = contentBoxSnap.data() || {};

      // Mirror the legacy background field while saving the canonical sparse override.
      const saveResult = await updateExistingContentBoxDoc({
        userId,
        classId,
        contentBoxId,
        payload: {
          background: imageFileName,
          styleOverrides: buildStyleOverridesWithSection(existingContent.styleOverrides, "background", { value: imageFileName }),
        },
        metricName: "content_box.update",
      });
      if (!saveResult.ok) {
        return false;
      }
    }

    console.log("✅ Content box background updated successfully.");
    return true;
  } catch (error) {
    if (isMissingContentBoxUpdateError(error)) {
      trackAppMetric("content_box.update", {
        outcome: "stale",
        reason: "not_found_update",
        action: "save_background",
      });
      return false;
    }
    console.error("❌ Error updating content box background:", error);
    throw error;
  }
};

/**
 * Updates the active start and end dates of a content box in Firestore.
 *
 * @param {string} userId - The ID of the user.
 * @param {string} classId - The ID of the class containing the content box.
 * @param {string} contentBoxId - The ID of the content box to update.
 * @param {Object} activeDates - Object containing `startDate` and `endDate`.
 * @param {Function} toggleSetActiveDatesModal - Function to close the active dates modal.
 *
 * @returns {Promise<boolean>} A promise that resolves to true if successful, false if failed.
 */
 export const saveContentBoxActiveDates = async (
    userId,
    classId,
    contentBoxId,
    activeDates,
    toggleSetActiveDatesModal,
    options = {}
  ) => {
    try {
      const hasRecurringStartDate = Array.isArray(activeDates?.startDate) && activeDates.startDate.length > 0;
      const hasFixedStartDate = !Array.isArray(activeDates?.startDate) && Boolean(activeDates?.startDate);
      const hasValidEndDate = hasRecurringStartDate || Boolean(activeDates?.endDate);

      if (!userId || !classId || !contentBoxId || (!hasRecurringStartDate && !hasFixedStartDate) || !hasValidEndDate) {
        console.error("❌ Missing required parameters: userId, classId, contentBoxId, startDate, or endDate.");
        return false;
      }
  
      const saveResult = await saveContentBoxActiveDatesWithLinkedScope({
        userId,
        classId,
        contentBoxId,
        activeDates: {
          startDate: activeDates.startDate,
          endDate: activeDates.endDate,
        },
        contentBox: options.contentBox,
        classes: options.classes,
        scopeClassIds: options.scopeClassIds,
        actionLabel: options.actionLabel || "change active dates",
      });

      if (!saveResult?.ok) {
        if (options.returnResult) {
          return saveResult;
        }
        return false;
      }
  
      // Close the modal after saving (if function is provided)
      if (toggleSetActiveDatesModal) {
        toggleSetActiveDatesModal();
      }
  
      console.log("✅ Content box active dates updated successfully.");
      if (options.returnResult) {
        return saveResult;
      }
      return true;
    } catch (error) {
      if (isMissingContentBoxUpdateError(error)) {
        trackAppMetric("content_box.update", {
          outcome: "stale",
          reason: "not_found_update",
          action: "save_active_dates",
        });
        return false;
      }
      console.error("❌ Error updating active dates:", error);
      throw error;
    }
  };
  
  /**
   * Adjusts the zoom level of a content box.
   * @param {string} userId - The user's ID.
   * @param {string} classId - The class ID containing the content box.
   * @param {string} contentBoxId - The content box ID.
   * @param {string} direction - "up" to zoom in, "down" to zoom out.
   * @param {boolean} multiBox - Whether this is a multi-box content.
   * @param {Function} updateContentBoxWithinMultiBox - Function to update multi-box state.
   * @param {{ absoluteZoom?: number }} [options] - When absoluteZoom is set, persist that value
   *   instead of re-reading and incrementing (avoids races with optimistic UI).
   * @returns {Promise<number|boolean>} - Returns the new zoom level if successful, false if failed
   */
  export const zoomContentBox = async (
    userId,
    classId,
    contentBoxId,
    direction,
    multiBox = false,
    updateContentBoxWithinMultiBox,
    options = {}
  ) => {
    try {
      if (!userId || !classId || !contentBoxId) {
        console.error("❌ Missing required identifiers: user ID, class ID, or content box ID.");
        return false;
      }
  
      const zoomIncrement = direction === "up" ? 0.1 : -0.1;
      const hasAbsoluteZoom = Number.isFinite(Number(options?.absoluteZoom));
  
      if (multiBox) {
        console.log("Yes, it's a multibox")
        // Handle multiBox logic
        const previousZoom = content.zoom || 1;
        const newZoom = hasAbsoluteZoom
          ? clampContentBoxZoom(Number(options.absoluteZoom))
          : clampContentBoxZoom(Number((previousZoom + zoomIncrement).toFixed(2)));
  
        updateContentBoxWithinMultiBox({ ...content, zoom: newZoom }, true);
        console.log(`✅ MultiBox zoom updated to ${newZoom}.`);
        return newZoom;
      } else {
        const contentBoxRef = doc(
          db,
          FIREBASE_COLLECTIONS.USERS,
          userId,
          FIREBASE_COLLECTIONS.CLASSES,
          classId,
          FIREBASE_COLLECTIONS.CONTENT_BOXES,
          contentBoxId
        );

        let newZoom;
        if (hasAbsoluteZoom) {
          newZoom = clampContentBoxZoom(Number(options.absoluteZoom));
        } else {
          const contentBoxSnap = await getDoc(contentBoxRef);
  
          if (!contentBoxSnap.exists()) {
            trackAppMetric("content_box.update", {
              outcome: "stale",
              reason: "missing_doc",
              action: "zoom",
            });
            console.error("❌ Content box document not found.");
            return false;
          }
  
          const contentBoxData = contentBoxSnap.data();
          const previousZoom = contentBoxData.zoom || 1;
          newZoom = clampContentBoxZoom(Number((previousZoom + zoomIncrement).toFixed(2)));
        }

        const saveResult = await updateExistingContentBoxDoc({
          userId,
          classId,
          contentBoxId,
          payload: { zoom: newZoom },
          metricName: "content_box.update",
        });
        if (!saveResult.ok) {
          return false;
        }
  
        console.log(`✅ Content box zoom updated to ${newZoom}.`);
        return newZoom;
      }
    } catch (error) {
      if (isMissingContentBoxUpdateError(error)) {
        trackAppMetric("content_box.update", {
          outcome: "stale",
          reason: "not_found_update",
          action: "zoom",
        });
        return false;
      }
      console.error("❌ Error updating content box zoom:", error);
      throw error;
    }
};

/**
 * Updates a content box in Firestore.
 * @param {string} userId - The user's ID.
 * @param {string} classId - The class ID containing the content box.
 * @param {string} contentBoxId - The content box ID.
 * @param {object} updatedContent - The updated content box data.
 * @param {Function} setBoxes - Function to update local state.
 * @returns {Promise<boolean>} - Returns true if successful, false if failed
 */
export const updateContentBox = async (
  userId,
  classId,
  contentBoxId,
  updatedContent,
  setBoxes
) => {
  try {
    if (!userId || !classId || !contentBoxId) {
      console.error("❌ Missing required identifiers: user ID, class ID, or content box ID.");
      return false;
    }

    // Reference the specific content box document in Firestore
    const contentBoxRef = doc(
      db,
      FIREBASE_COLLECTIONS.USERS,
      userId,
      FIREBASE_COLLECTIONS.CLASSES,
      classId,
      FIREBASE_COLLECTIONS.CONTENT_BOXES,
      contentBoxId
    );

    const contentBoxSnap = await getDoc(contentBoxRef);
    if (!contentBoxSnap.exists()) {
      trackAppMetric("content_box.update", {
        outcome: "stale",
        reason: "missing_doc",
        action: "save",
      });
      console.error("❌ Content box not found.");
      return false;
    }

    let updatedContentBox = { ...updatedContent };

    // Only normalize heading when the caller is explicitly changing it.
    // Content-body saves must not clobber tab names on boxes missing userHeading.
    if (
      Object.prototype.hasOwnProperty.call(updatedContent || {}, "heading")
      && !updatedContentBox.userHeading
    ) {
      if (updatedContentBox.heading === undefined) {
        updatedContentBox.heading = "Heading";
      } else if (updatedContentBox.heading.length === 0) {
        updatedContentBox.heading = "Type here...";
      }
    }

    const saveResult = await updateExistingContentBoxDoc({
      userId,
      classId,
      contentBoxId,
      payload: updatedContentBox,
      metricName: "content_box.update",
    });
    if (!saveResult.ok) {
      return false;
    }

    // 🔁 If this is a linked box, propagate to other classes
    if (updatedContentBox.linkedBox) {
      await updateLinkedContentBoxes(userId, contentBoxId, updatedContentBox, {
        sourceClassId: classId,
      });
    }

    // Update local state
    if (setBoxes) {
      setBoxes((prevBoxes) =>
        prevBoxes.map((box) => (box.contentBoxId === contentBoxId ? { ...box, ...updatedContentBox } : box))
      );
    }

    console.log("✅ Content box updated successfully!");
    return true;
  } catch (error) {
    if (isMissingContentBoxUpdateError(error)) {
      trackAppMetric("content_box.update", {
        outcome: "stale",
        reason: "not_found_update",
        action: "save",
      });
      return false;
    }
    console.error("❌ Error updating content box:", error);
    reportAppError(error, {
      feature: "content_box",
      action: "save",
      severity: "error",
      classId,
      contentBoxId,
      uid: userId,
      contentBoxType: updatedContent?.contentBoxType || updatedContent?.type || updatedContent?.boxType,
      extra: {
        updatedKeys: Object.keys(updatedContent || {}),
        linkedBox: Boolean(updatedContent?.linkedBox),
      },
    });
    throw error;
  }
};

/**
 * Unlinks a linked content box in a single class.
 * This detaches future updates from other classes while preserving current content.
 *
 * @param {string} userId - The user's UID.
 * @param {string} classId - The class ID containing the box to unlink.
 * @param {string} contentBoxId - The ID of the content box to unlink.
 * @returns {Promise<boolean>} - Returns true if successful, false if failed
 */
export const unlinkContentBox = async (userId, classId, contentBoxId, options = {}) => {
  try {
    if (!userId || !classId || !contentBoxId) {
      console.error("❌ Missing required parameters for unlinkContentBox");
      return false;
    }

    const { classes } = options;
    const contentBoxRef = getContentBoxRef(userId, classId, contentBoxId);

    const contentBoxSnap = await getDoc(contentBoxRef);
    if (!contentBoxSnap.exists()) {
      console.error("❌ Content box not found for unlink.");
      return false;
    }

    const currentBoxData = contentBoxSnap.data();
    if (!currentBoxData?.linkedBox) {
      return true;
    }

    const classRef = getClassRef(userId, classId);
    let currentOrder;
    try {
      currentOrder = await fetchClassContentBoxesOrder(userId, classId);
    } catch (error) {
      console.error("❌ Class not found while unlinking content box.", error);
      return false;
    }

    const newContentBoxId =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    const newContentBoxRef = getContentBoxRef(userId, classId, newContentBoxId);

    const {
      linkedSlidePosition: _removedLinkedSlidePosition,
      linkedInClasses: _removedLinkedInClasses,
      linkedClassIds: _removedLinkedClassIds,
      ...baseDetachedData
    } = currentBoxData;

    const detachedBoxData = {
      ...baseDetachedData,
      contentBoxId: newContentBoxId,
      linkedBox: false,
      useLinkedSlidePosition: false,
    };

    const updatedOrder = replaceContentBoxIdInOrder(currentOrder, contentBoxId, newContentBoxId);

    const batch = writeBatch(db);
    batch.set(newContentBoxRef, detachedBoxData);
    batch.update(classRef, { contentBoxesOrder: updatedOrder });
    batch.delete(contentBoxRef);
    await batch.commit();

    const remainingLinkedClassIds = await resolveLinkedClassIds(userId, contentBoxId, {
      sourceClassId: classId,
      classIds: (classes || []).map((item) => item?.classId).filter(Boolean),
      hintLinkedClassIds: currentBoxData.linkedClassIds,
    });
    const nextLinkedClassIds = remainingLinkedClassIds.filter(
      (targetClassId) => targetClassId !== classId
    );
    await syncLinkedClassIdsIndex(userId, contentBoxId, nextLinkedClassIds);

    console.log(
      `✅ Hard-unlinked content box ${contentBoxId} -> ${newContentBoxId} in class ${classId}`
    );
    return true;
  } catch (error) {
    console.error("❌ Error unlinking content box:", error);
    throw error;
  }
};

  

